import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { CartItem } from '../models';
import { trackAddToCart } from '../utils/metaPixel';
import { StoreEventType, trackStoreEvent } from '../services/trackingService';

function trackCartEvent(type: StoreEventType, item: CartItem): void {
  trackStoreEvent({
    type,
    productId: item.productId,
    productVariantId: item.variantId > 0 ? item.variantId : undefined,
    metadata: { quantity: item.quantity },
  });
}

interface CartStore {
  items: CartItem[];
  addItem: (item: CartItem) => 'added' | 'updated' | 'max_stock';
  removeItem: (productId: number, variantId: number) => void;
  updateQuantity: (productId: number, variantId: number, quantity: number) => void;
  applyAvailableStock: (variantId: number, available: number) => void;
  syncVariant: (variantId: number, available: number, variationTypeName?: string) => void;
  restoreItem: (item: CartItem, index: number) => void;
  toggleGift: (productId: number, variantId: number) => void;
  clearCart: () => void;
  totalItems: () => number;
  totalPrice: () => number;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],

      addItem: (item) => {
        const existing = get().items.find(
          (i) => i.productId === item.productId && i.variantId === item.variantId
        );
        if (existing) {
          const newQty = existing.quantity + item.quantity;
          if (newQty > item.stockQuantity) return 'max_stock';
          set((state) => ({
            items: state.items.map((i) =>
              i.productId === item.productId && i.variantId === item.variantId
                ? { ...i, quantity: newQty, stockQuantity: item.stockQuantity }
                : i
            ),
          }));
          trackAddToCart(item);
          trackCartEvent(StoreEventType.AddToCart, item);
          return 'updated';
        }
        if (item.quantity > item.stockQuantity) return 'max_stock';
        set((state) => ({ items: [...state.items, item] }));
        trackAddToCart(item);
        trackCartEvent(StoreEventType.AddToCart, item);
        return 'added';
      },

      removeItem: (productId, variantId) => {
        const removed = get().items.find(
          (i) => i.productId === productId && i.variantId === variantId
        );
        set((state) => ({
          items: state.items.filter(
            (i) => !(i.productId === productId && i.variantId === variantId)
          ),
        }));
        if (removed) trackCartEvent(StoreEventType.RemoveFromCart, removed);
      },

      updateQuantity: (productId, variantId, quantity) =>
        set((state) => ({
          items: state.items.map((i) => {
            if (i.productId !== productId || i.variantId !== variantId) return i;
            const capped = Math.min(quantity, i.stockQuantity);
            return { ...i, quantity: Math.max(1, capped) };
          }),
        })),

      applyAvailableStock: (variantId, available) =>
        set((state) => ({
          items:
            available <= 0
              ? state.items.filter((i) => i.variantId !== variantId)
              : state.items.map((i) =>
                  i.variantId === variantId
                    ? { ...i, stockQuantity: available, quantity: Math.min(i.quantity, available) }
                    : i
                ),
        })),

      syncVariant: (variantId, available, variationTypeName) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.variantId === variantId
              ? {
                  ...i,
                  stockQuantity: Math.max(0, available),
                  variationTypeName: variationTypeName ?? i.variationTypeName,
                }
              : i
          ),
        })),

      restoreItem: (item, index) =>
        set((state) => {
          const exists = state.items.some(
            (i) => i.productId === item.productId && i.variantId === item.variantId
          );
          if (exists) return state;
          const next = [...state.items];
          next.splice(Math.min(Math.max(index, 0), next.length), 0, item);
          return { items: next };
        }),

      toggleGift: (productId, variantId) =>
        set((state) => ({
          items: state.items.map((i) =>
            i.productId === productId && i.variantId === variantId
              ? { ...i, isGift: !i.isGift }
              : i
          ),
        })),

      clearCart: () => set({ items: [] }),

      totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),

      totalPrice: () =>
        get().items.reduce(
          (sum, i) => sum + (i.promotionalPrice ?? i.price) * i.quantity,
          0
        ),
    }),
    { name: 'babychic-cart' }
  )
);
